import {
  DetailsRow,
  IColumn,
  Icon,
  IDetailsRowProps,
  IGroup,
  IGroupDividerProps,
  IObjectWithKey,
  MessageBar,
  MessageBarType,
  Selection,
  SelectionMode,
  ShimmeredDetailsList,
  TooltipHost,
} from '@fluentui/react';
import * as _ from 'lodash';
import * as React from 'react';
import { Market } from '../../api/markets.types';
import { HealAPI } from '../../healApi';
import { AsyncData } from '../../redux/core';
import { ToastParams } from '../../redux/message/types';
import { MAShift, MessageStatus, NotAllowedMatch, Provider, ScheduleEdit, Shift, ShiftSubschedule as ShiftSubscheduleType } from '../../redux/scheduling/types';
import { UIState } from '../../redux/ui/types';
import { css } from '../../util/Css';
import * as DateUtil from '../../util/Date';
import { TrackingTextField } from '../core/TrackingTextField';
import { FloatMAList } from './FloatMAList';
import { LocalDateRange } from './LocalDateRange';
import { MASelector } from './MASelector';
import { ShiftMarketGroupHeader } from './ShiftGroupMarketHeader';
import './ShiftSubschedule.scss';
import { groupCodeToName } from './Util';

const NO_MA_ASSIGNED = -1;
const MA_UNAVAILABLE = -2;

interface ShiftSubscheduleProps {
  onSelectFloatMA?: (Mas: IObjectWithKey[], shiftType: string) => void;
  actions: {
    onSelectionChanged?: () => void;
    toast?: (params: ToastParams) => void;
  };
  pairingRules: AsyncData<NotAllowedMatch[]>;
  shiftType: string;
  allMarketGroupsSorted: string[];
  markets: Market[];
  highlightedShiftId?: string;
  isFetching: boolean;
  selectionEnabled?: boolean;
  subschedule?: ShiftSubscheduleType;
  editHistory?: ScheduleEdit[];
  ui: UIState;
}

interface ShiftSubscheduleState {
  maAssignmentCount: {
    [maId: string]: {
      count: number;
      ma: Provider;
    };
  };
  masById: {
    [maId: string]: MAShift;
  };
  shifts: Shift[];
  shiftRecentlySaved: {
    [shiftId: string]: boolean;
  };
}

export class ShiftSubschedule extends React.Component<ShiftSubscheduleProps, ShiftSubscheduleState> {
  public static defaultProps: Partial<ShiftSubscheduleProps> = {
    actions: {},
    pairingRules: { data: [], isFetching: false },
    allMarketGroupsSorted: [],
    markets: [],
    isFetching: false,
  };
  private selection: Selection | undefined;
  private timer: number | undefined;

  public constructor(props: ShiftSubscheduleProps) {
    super(props);

    this.selection = new Selection({
      canSelectItem: (shift: any) => {
        const assignedMAShiftCount = this.assignedMAShiftCount(shift);
        return (
          this.shiftIsLocked(shift) === false &&
          this.shiftDocIsAvailable(shift) &&
          assignedMAShiftCount !== MA_UNAVAILABLE &&
          assignedMAShiftCount < 2
        );
      },
    });

    this.state = {
      maAssignmentCount: {},
      masById: {},
      shifts: [],
      shiftRecentlySaved: {},
    };
  }

  public componentDidUpdate(prevProps: ShiftSubscheduleProps) {
    if (this.props.subschedule !== prevProps.subschedule) {
      const nextShifts = this.props.subschedule ? this.props.subschedule.shifts : [];
      const nextScheduledMAs = this.props.subschedule ? this.props.subschedule.scheduledMAs : [];
      this.setState({
        maAssignmentCount: this.buildMAAssignmentCount(nextShifts, nextScheduledMAs),
        masById: this.buildMAMap(nextScheduledMAs),
        shifts: nextShifts,
      });
    }
  }

  public componentWillUnmount() {
    clearTimeout(this.timer);
  }

  public render(): JSX.Element {
    const rows = this.buildRows();
    // NOTE we could calculate these as we go with buildMAAssignmentCount,
    //  more efficient and then can use it to enable/disable the send msgs button if doubles exist
    const doubleAssignedMAs = this.getDoubleAssignedMAs();
    const floatMAs = this.getFloatMAShifts();

    const shiftsByMarket: { [market: string]: Shift[] } = {};
    rows.forEach((shift) => {
      if (shiftsByMarket[shift.marketCode]) {
        shiftsByMarket[shift.marketCode].push(shift);
      } else {
        shiftsByMarket[shift.marketCode] = [shift];
      }
    });

    const shiftGroups: IGroup[] = [];
    let startIndex = 0;
    const sortedGroupedShifts = _.flatMap(this.props.allMarketGroupsSorted, (marketCode: string) => {
      const value = shiftsByMarket[marketCode];
      if (value == null) {
        return [];
      }
      shiftGroups.push({
        key: marketCode,
        name: groupCodeToName(marketCode),
        isCollapsed: this.props.ui.schedulingExpand.indexOf(marketCode) === -1,

        startIndex: startIndex,
        count: value.length,
      });
      startIndex += value.length;
      return value;
    });

    return (
      <span className="shift-subschedule">
        {doubleAssignedMAs.length > 0 && (
          <MessageBar messageBarType={MessageBarType.severeWarning} isMultiline={false}>
            The following MAs have been assigned to multiple simultaneous shifts:&nbsp;
            <i>
              {doubleAssignedMAs
                .map((ma) => {
                  return `${ma.name} ${ma.surname} (${ma.marketCode})`;
                })
                .join(', ')}
            </i>
          </MessageBar>
        )}
        {floatMAs.length > 0 && (
          <MessageBar messageBarType={MessageBarType.warning} isMultiline={false}>
            The following MAs are scheduled to work but have not been assigned:&nbsp;&nbsp;
            <i>
              {floatMAs
                .map((ma) => {
                  return `${ma.name} ${ma.surname} (${ma.marketCode})`;
                })
                .join(', ')}
            </i>
          </MessageBar>
        )}
        <div className="shift-list">
          <div className="title">Shifts</div>
          <ShimmeredDetailsList
            columns={this.buildColumns()}
            enableShimmer={this.props.isFetching}
            groups={shiftGroups}
            groupProps={{
              onRenderHeader: this.onRenderGroupHeader,
            }}
            items={sortedGroupedShifts}
            onRenderRow={this.onRenderRow}
            selection={this.selection}
            selectionMode={this.props.selectionEnabled ? SelectionMode.multiple : SelectionMode.none}
            shimmerLines={6}
            compact
            selectionPreservedOnEmptyClick
          />
          {!this.props.isFetching && rows.length === 0 && <div className="no-results">No shifts found for current filters.</div>}
        </div>
        <div className="shift-list floats">
          <div className="title">Float MAs</div>
          <FloatMAList
            shiftType={this.props.shiftType}
            floatMAShifts={floatMAs}
            onSelectFloatMA={(MAs: IObjectWithKey[]) => this.onSelectFloatMA(MAs, this.props.shiftType)}
            floatMessages={this.props.subschedule ? this.props.subschedule.floatMessages || {} : {}}
            maNotes={this.props.subschedule ? this.props.subschedule.maNotes || {} : {}}
            actions={this.props.actions}
          />
          {!this.props.isFetching && floatMAs.length === 0 && <div className="no-results">No float MAs for this subschedule.</div>}
        </div>
      </span>
    );
  }

  private onRenderGroupHeader = (props?: IGroupDividerProps): JSX.Element => {
    return <ShiftMarketGroupHeader {...props!} />;
  };

  public onSelectFloatMA = (MAs: IObjectWithKey[], shiftType: string) => {
    if (this.props.onSelectFloatMA) {
      this.props.onSelectFloatMA(MAs, shiftType);
    }
  };

  public getSelection(): IObjectWithKey[] {
    if (this.selection) {
      return this.selection.getSelection();
    }
    return [];
  }

  public getFloatMAShifts(): MAShift[] {
    if (!this.props.subschedule) {
      return [];
    }
    const maAccountedFor: { [key: string]: boolean } = {};
    const result = this.props.subschedule.scheduledMAs.map((o) => {
      const key = Object.keys(o)[0];
      const mas = o[key].filter((ma) => {
        if (maAccountedFor[ma.id]) {
          return false;
        }
        maAccountedFor[ma.id] = true;
        return this.state.maAssignmentCount[ma.id] && this.state.maAssignmentCount[ma.id].count === 0 && !ma.isOnsite;
      });
      mas.forEach((ma) => {
        ma.marketCode = key;
      });
      return mas;
    });
    return ([] as MAShift[]).concat(...result);
  }

  private assignedMAShiftCount(shift: Shift): number {
    const maId = (shift.ma && shift.ma.id) || '';
    if (!shift.ma) {
      return NO_MA_ASSIGNED;
    }
    if (!(this.state.masById[maId] && this.state.maAssignmentCount[maId])) {
      return MA_UNAVAILABLE;
    }
    return this.state.maAssignmentCount[maId].count;
  }

  private buildColumns(): IColumn[] {
    return [
      {
        key: 'submarkets',
        name: 'Submarkets',
        fieldName: 'submarkets',
        minWidth: 140,
        maxWidth: 140,
        isResizable: true,
        onRender: (shift: Shift) => {
          const tooltip = (shift.submarkets || [])
            .map((marketCode) => {
              const name = this.props.markets.find((m) => m.code === marketCode)?.name;
              if (name) {
                return `${name} (${marketCode})`;
              }
              return marketCode;
            })
            .join(', ');
          return <TooltipHost content={tooltip}>{shift.submarkets.length > 0 ? shift.submarkets.join(', ') : '-'}</TooltipHost>;
        },
      },
      {
        key: 'drType',
        name: 'Type',
        fieldName: '',
        minWidth: 40,
        maxWidth: 40,
        onRender: (shift: Shift) => {
          return shift.doctor?.type || 'N/A';
        },
      },
      {
        key: 'doctorConfirmed',
        name: '',
        fieldName: '',
        minWidth: 10,
        maxWidth: 10,
        onRender: (shift: Shift) => {
          if (shift.drMessageStatus === MessageStatus.Draft) {
            return null;
          }
          return (
            <TooltipHost content={`Message ${shift.drMessageStatus}`}>
              <div className={`message-status-badge ${shift.drMessageStatus}`} />
            </TooltipHost>
          );
        },
      },
      {
        key: 'doctorName',
        name: 'Provider',
        fieldName: '',
        minWidth: 100,
        maxWidth: 100,
        onRender: (shift: Shift) => {
          if (shift.doctor) {
            const tooltipContent = this.shiftDocIsAvailable(shift) ? [shift.doctor.prefix, shift.doctor.name, shift.doctor.surname].join(' ') : 'Doctor is no longer available';
            return (
              <div
                className="name-container"
                style={{
                  color:
                    shift.ma && shift.doctor && (this.props.pairingRules.data || []).some((pr) => pr.providerOneId === shift.doctor!.id && pr.providerTwoId === shift.ma!.id)
                      ? 'red'
                      : '',
                }}
              >
                <TooltipHost content={tooltipContent}>
                  {shift.doctor.name[0]}. {shift.doctor.surname}
                </TooltipHost>
              </div>
            );
          } else {
            return 'OPEN';
          }
        },
      },
      {
        key: 'positionType',
        name: 'Position',
        fieldName: '',
        minWidth: 80,
        maxWidth: 80,
        onRender: (shift: Shift) => shift.positionType || 'N/A',
      },
      {
        key: 'drDateRange',
        name: 'Provider Shift',
        fieldName: '',
        minWidth: 110,
        maxWidth: 110,
        onRender: (shift: Shift) => <LocalDateRange {...shift.drDateRange} />,
      },
      {
        key: 'maConfirmed',
        name: '',
        fieldName: '',
        minWidth: 10,
        maxWidth: 10,
        onRender: (shift: Shift) => {
          if (shift.maMessageStatus === MessageStatus.Draft) {
            return null;
          }
          return (
            <TooltipHost content={`Message ${shift.maMessageStatus}`}>
              <div className={`message-status-badge ${shift.maMessageStatus}`} />
            </TooltipHost>
          );
        },
      },
      {
        key: 'maName',
        name: 'Assistant',
        fieldName: '',
        minWidth: 90,
        maxWidth: 90,
        onRender: (shift: Shift) => {
          const assignedMAShiftCount = this.assignedMAShiftCount(shift);
          let tooltipContent;
          switch (assignedMAShiftCount) {
            case MA_UNAVAILABLE:
              tooltipContent = 'Assigned MA is no longer available';
              break;
            case NO_MA_ASSIGNED:
              tooltipContent = 'No MA assigned to this shift';
              break;
            case 0:
            case 1:
              tooltipContent = shift.ma && `${shift.ma.name} ${shift.ma.surname}`;
              break;
            default:
              tooltipContent = 'MA has multiple simultaneous assignments';
              break;
          }

          const maSurname = shift.ma && shift.ma.surname ? shift.ma.surname[0] : '?';
          let maContent;
          let searchableClass = '';
          if (this.shiftIsLocked(shift)) {
            maContent = shift.ma ? `${shift.ma.name} ${maSurname}.` : 'Unassigned';
          } else {
            maContent = (
              <MASelector
                onChange={(ma) => {
                  let maId;
                  if (ma) {
                    maId = ma.id;
                  }
                  this.setMAforShift(shift.id, maId);
                }}
                scheduledMAsByMarket={(this.props.subschedule && this.props.subschedule.scheduledMAs) || []}
                selectedMA={shift.ma as Provider}
                shiftMarket={shift.marketCode}
              />
            );
            searchableClass = 'searchable';
          }
          return (
            <div
              className={`name-container ${searchableClass}`}
              style={{
                color:
                  shift.ma && shift.doctor && (this.props.pairingRules.data || []).some((pr) => pr.providerOneId === shift.doctor!.id && pr.providerTwoId === shift.ma!.id)
                    ? 'red'
                    : '',
              }}
            >
              <TooltipHost content={tooltipContent || ''}>{maContent}</TooltipHost>
            </div>
          );
        },
      },
      {
        key: 'maDateRange',
        name: 'Assistant Shift',
        fieldName: '',
        minWidth: 110,
        maxWidth: 110,
        onRender: (shift: Shift) => {
          if (!shift.ma) {
            return 'TBD';
          }
          if (shift.ma.shiftDateRange) {
            return <LocalDateRange {...shift.ma.shiftDateRange} />;
          }
          if (shift.maDateRange) {
            // Legacy
            return <LocalDateRange {...shift.maDateRange} />;
          }
          return 'TBD';
        },
      },
      {
        key: 'badges',
        name: '',
        fieldName: '',
        minWidth: 40,
        maxWidth: 40,
        onRender: (shift: Shift) => {
          let nurseIcon;
          if (this.isNurseShift(shift)) {
            nurseIcon = (
              <TooltipHost content="Nurse shift">
                <Icon iconName="Hospital" />
              </TooltipHost>
            );
          }

          let onsiteIcon;
          if (shift.isOnsite) {
            onsiteIcon = (
              <TooltipHost content="Onsite shift">
                <Icon iconName="CityNext" />
              </TooltipHost>
            );
          }
          return (
            <span>
              {nurseIcon}
              {onsiteIcon}
            </span>
          );
        },
      },
      {
        key: 'notes',
        name: 'Notes',
        fieldName: 'notes',
        minWidth: 170,
        maxWidth: 170,
        isMultiline: true,
        onRender: (shift: Shift) => (
          <TooltipHost content={shift.notes}>
            <TrackingTextField
              placeholder="Enter notes"
              defaultValue={shift.notes}
              rows={1}
              onFinishedUpdate={(notes) => this.setNotesforShift(shift.id, notes)}
              borderless
              multiline
              autoAdjustHeight
            />
          </TooltipHost>
        ),
      },
    ];
  }

  private buildMAAssignmentCount(
    shifts: Shift[],
    scheduledMAs: { [market: string]: MAShift[] }[]
  ): {
    [maId: string]: {
      count: number;
      ma: Provider;
    };
  } {
    const result: { [key: string]: { count: number; ma: MAShift } } = {};
    shifts.forEach((shift: Shift) => {
      if (!shift.ma) {
        return;
      }
      if (!(shift.ma.id in result)) {
        result[shift.ma.id] = {
          count: 0,
          ma: shift.ma,
        };
      }
      result[shift.ma.id].count += 1;
    });

    scheduledMAs.forEach((o) => {
      const key = Object.keys(o)[0];
      o[key].forEach((ma) => {
        if (!(ma.id in result)) {
          result[ma.id] = {
            count: 0,
            ma: ma,
          };
        }
      });
    });
    return result;
  }

  private buildMAMap(scheduledMAs: { [market: string]: MAShift[] }[]): { [id: string]: MAShift } {
    const maMap: { [key: string]: MAShift } = {};
    scheduledMAs.forEach((o) => {
      const key = Object.keys(o)[0];
      o[key].forEach((ma) => {
        maMap[ma.id] = ma;
      });
    });
    return maMap;
  }

  private buildRows(): Shift[] {
    if (this.props.isFetching) {
      return [];
    }
    return this.state.shifts;
  }

  private getDoubleAssignedMAs(): Provider[] {
    if (!this.props.subschedule) {
      return [];
    }
    const maAccountedFor: { [key: string]: boolean } = {};
    let result = this.props.subschedule.scheduledMAs.map((o) => {
      const key = Object.keys(o)[0];
      const mas = o[key].filter((ma) => {
        if (maAccountedFor[ma.id]) {
          return false;
        }
        maAccountedFor[ma.id] = true;
        return this.state.maAssignmentCount[ma.id] && this.state.maAssignmentCount[ma.id].count > 1;
      });
      mas.forEach((ma) => {
        ma.marketCode = key;
      });
      return mas;
    });
    result = _.uniqBy(result, 'id');
    return ([] as Provider[]).concat(...result);
  }

  private getOnsiteMAShifts(): MAShift[] {
    if (!this.props.subschedule) {
      return [];
    }
    let result = this.props.subschedule.scheduledMAs
      .map((o) => {
        const key = Object.keys(o)[0];
        const mas = o[key].filter((ma) => {
          return ma.isOnsite;
        });
        return mas;
      })
      .filter((o) => o.length > 0);

    result = _.uniqBy(result, 'id');
    return ([] as MAShift[]).concat(...result);
  }

  private isNurseShift(shift: Shift): boolean {
    if (shift.doctor == null) {
      return false;
    }
    let suffix = '';
    if (shift.doctor.suffix) {
      suffix = shift.doctor.suffix.toLowerCase().trim();
    }

    return suffix.indexOf('rn') >= 0 || suffix.indexOf('lvn') >= 0;
  }

  private setMAforShift(shiftId: string, maId?: string) {
    const shiftRecentlySaved = { ...this.state.shiftRecentlySaved };
    const shifts = [...this.state.shifts];

    const shift = shifts.find((s) => s.id === shiftId);
    if (shift) {
      const prevMaId = shift.ma && shift.ma.id;
      if (prevMaId !== maId) {
        shift.drMessageStatus = MessageStatus.Draft;
        shift.maMessageStatus = MessageStatus.Draft;
      }
      if (maId) {
        shift.ma = this.state.masById[maId];
      } else {
        shift.ma = null;
      }
      shiftRecentlySaved[shiftId] = true;
    }
    maId = maId || 'none';
    this.updateShiftsWithSave(shiftId, shifts, shiftRecentlySaved);
    HealAPI.setMAForShift(shiftId, maId).then().catch(this.displayError);
  }

  private setNotesforShift(shiftId: string, notes: string) {
    const shiftRecentlySaved = { ...this.state.shiftRecentlySaved };
    const shifts = [...this.state.shifts];
    const trimmedNotes = notes.trim();

    const shift = shifts.find((s) => s.id === shiftId);
    if (shift) {
      shift.notes = trimmedNotes;
      shiftRecentlySaved[shiftId] = true;
    }
    this.updateShiftsWithSave(shiftId, shifts, shiftRecentlySaved);
    HealAPI.setNotesForShift(shiftId, trimmedNotes).then().catch(this.displayError);
  }

  private shiftDocIsAvailable(shift: Shift): boolean {
    if (!this.props.subschedule || !shift.doctor) {
      return false;
    }
    return this.props.subschedule.scheduledDoctors.some((id) => id === shift.doctor!.id);
  }

  private shiftIsLocked(shift: Shift) {
    const endDate = new Date(shift.drDateRange.endTime * 1000);
    return DateUtil.isInPast(endDate);
  }

  private updateShiftsWithSave(
    shiftId: string,
    shifts: Shift[],
    shiftRecentlySaved: {
      [shiftId: string]: boolean;
    }
  ) {
    const scheduledMAs = this.props.subschedule ? this.props.subschedule.scheduledMAs : [];
    this.setState({
      maAssignmentCount: this.buildMAAssignmentCount(shifts, scheduledMAs),
      shifts: shifts,
      shiftRecentlySaved: shiftRecentlySaved,
    });

    this.timer = window.setTimeout(() => {
      const recentlySaved = { ...this.state.shiftRecentlySaved };
      delete recentlySaved[shiftId];
      this.setState({
        shiftRecentlySaved: recentlySaved,
      });
    }, 1000);
  }

  private displayError = (error: string) => {
    if (this.props.actions.toast) {
      return this.props.actions.toast({ type: 'error', title: 'Error', content: error });
    }
  };

  private onRenderRow = (props?: IDetailsRowProps): JSX.Element => {
    props = props!;
    const assignedMAShiftCount = this.assignedMAShiftCount(props.item);
    const rowClass = css({
      'changes-saved': this.state.shiftRecentlySaved[props.item.id],
      'onsite-shift': props.item.isOnsite,
      highlighted: this.props.highlightedShiftId === props.item.id,
      locked: this.shiftIsLocked(props.item),
      'nurse-shift': this.isNurseShift(props.item),
      'double-assigned': assignedMAShiftCount > 1,
      unassigned: assignedMAShiftCount === NO_MA_ASSIGNED,
      'ma-unavailable': assignedMAShiftCount === MA_UNAVAILABLE,
      'doc-unavailable': !this.shiftDocIsAvailable(props.item),
      open: !props.item.doctor,
    });
    return <DetailsRow className={rowClass} {...props} />;
  };
}
